import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HttpResponse } from '../../common/ajax';
import { InvalidSession } from '../../common/components/ConnectionBroke';
import { MaintenanceApp } from '../../common/components/MaintenanceApp';
import { UnhealthyApp } from '../../common/components/UnhealthyApp';
import { useCustomerStatusErrorRedirect } from '../../common/hooks/useCustomerStatusErrorRedirect';
import useMount from '../../common/hooks/useMount';
import { logTrace } from '../../common/telemetry/logTrace';
import Loader from '../../common/widgets/Loader';
import LoadingSpinner from '../../common/widgets/LoadingSpinner';
import { config } from './config';
import { HealthyApp } from './global/components/HealthyApp';
import { getCspMeta } from './global/utils';
import './resources/i18n';
import { systemService } from './services/system';

const HEALTHY_CODE: string = 'Healthy';
const USER_STATUS_ERROR_CODES = new Set(['custom_419', 'custom_420']);

const HEALTH_CHECK_RETRIES = 3;
const HEALTH_CHECK_RETRY_DELAY_MS = 1000;

const isUserStatusError = (response: HttpResponse<string>) =>
  response.hasErrors && USER_STATUS_ERROR_CODES.has(response.errorCode ?? '');

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>(resolve => {
    const timeout = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });

// The API may not be reachable yet right after it (re)starts — e.g. in dev,
// when the browser opens before the backend listens (ERR_CONNECTION_REFUSED).
// Retry transient failures a few times before reporting the app as unhealthy.
const getWithRetry = async (
  request: () => Promise<HttpResponse<string>>,
  signal?: AbortSignal,
) => {
  let response = await request();

  for (
    let attempt = 1;
    attempt <= HEALTH_CHECK_RETRIES &&
    response.hasErrors &&
    response.errorCode !== 'ERR_CANCELED' &&
    !signal?.aborted;
    attempt++
  ) {
    logTrace('BenefitApp: health request failed, retrying', {
      attempt,
      errorCode: response.errorCode,
    });
    await wait(HEALTH_CHECK_RETRY_DELAY_MS * attempt, signal);
    if (signal?.aborted) {
      break;
    }
    response = await request();
  }

  return response;
};

export function BenefitApp() {
  useCustomerStatusErrorRedirect();
  const storageKey = `oidc.user:${config.idpAuthority}:${config.idpClientId}`;
  const hasInvalidSessionParam = new URL(
    globalThis.location.href,
  ).searchParams.has('invalid_session');
  const [isHealthyLoading, setIsHealthyLoading] = useState(true);
  const [isUnderMaintenanceLoading, setIsUnderMaintenanceLoading] =
    useState(true);
  const [isHealthy, setIsHealthy] = useState(true);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [isInvalidSession, setIsInvalidSession] = useState(
    hasInvalidSessionParam,
  );

  // 'ok': safe to render HealthyApp.
  // 'invalid': session is invalid, InvalidSession will be shown.
  // 'blocked': a global auth redirect (signout on customer-status-error, or
  //   an aborted check) is already under way elsewhere — render nothing and
  //   keep the loader up so we don't race that redirect with our own.
  type SessionHealthResult = 'ok' | 'invalid' | 'blocked';

  const validateSessionHealth = async (
    storageKey: string,
    abortController?: AbortController,
  ): Promise<SessionHealthResult> => {
    if (!localStorage[storageKey]) {
      return 'ok';
    }

    if (hasInvalidSessionParam) {
      return 'ok';
    }
    const sessionHealthResponse = await systemService.getSessionHealth({
      signal: abortController?.signal,
    });

    if (
      abortController?.signal.aborted ||
      isUserStatusError(sessionHealthResponse)
    ) {
      logTrace('BenefitApp: validateSessionHealth stopping early', {
        aborted: abortController?.signal.aborted,
        errorCode: sessionHealthResponse.errorCode,
      });
      return 'blocked';
    }

    if (
      sessionHealthResponse.hasErrors ||
      sessionHealthResponse.data != HEALTHY_CODE
    ) {
      /* user context validation failed, invalid access token or invalid use of it */
      logTrace('BenefitApp: session invalid, will show InvalidSession', {
        errorCode: sessionHealthResponse.errorCode,
      });
      setIsInvalidSession(true);
      return 'invalid';
    }

    logTrace('BenefitApp: session healthy');
    return 'ok';
  };

  const checkSystemHealth = async (abortController?: AbortController) => {
    logTrace('BenefitApp: checkSystemHealth start');

    const signal = abortController?.signal;
    const [healthResponse, maintenanceResponse] = await Promise.all([
      getWithRetry(() => systemService.getSystemHealth({ signal }), signal),
      getWithRetry(() => systemService.getSystemReadiness({ signal }), signal),
    ]);

    if (abortController?.signal.aborted) {
      logTrace('BenefitApp: checkSystemHealth aborted');
      return;
    }

    const healthy =
      !healthResponse.hasErrors && healthResponse.data == HEALTHY_CODE;
    setIsHealthy(healthy);

    const isMaintenanceMode =
      maintenanceResponse.hasErrors || maintenanceResponse.data != HEALTHY_CODE;

    logTrace('BenefitApp: health/maintenance results', {
      healthy,
      isMaintenanceMode,
    });

    if (isMaintenanceMode) {
      setIsUnderMaintenance(true);
      setIsHealthyLoading(false);
      setIsUnderMaintenanceLoading(false);
      return;
    }

    // Session validity must be known before HealthyApp is allowed to mount:
    // it starts its own silent auth-code redirect on mount, and letting that
    // race against a later setIsInvalidSession(true) causes two independent
    // window.location navigations (signinRedirect vs InvalidSession's reload)
    // that can cancel each other mid-flight.
    logTrace(
      'BenefitApp: validating session health before unlocking HealthyApp',
    );
    const sessionResult = await validateSessionHealth(
      storageKey,
      abortController,
    );

    if (sessionResult === 'blocked') {
      // A signout (or another global auth redirect) is already navigating
      // the page away — keep the loader up instead of unlocking rendering,
      // so nothing here (HealthyApp's signinRedirect, InvalidSession's own
      // location.replace) races that in-flight navigation.
      logTrace(
        'BenefitApp: session check blocked, keeping loader while auth redirect is in flight',
      );
      return;
    }

    setIsUnderMaintenance(false);
    setIsHealthyLoading(false);
    setIsUnderMaintenanceLoading(false);

    logTrace('BenefitApp: checkSystemHealth done, unlocking render', {
      sessionResult,
    });
  };

  useMount(checkSystemHealth);

  useMount(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('invalid_session')) {
      url.searchParams.delete('invalid_session');
      globalThis.history.replaceState({}, '', url);
    }
    if (url.searchParams.has('impersonate')) {
      const oidcStorage = localStorage.getItem(storageKey);
      if (oidcStorage) {
        localStorage.removeItem(storageKey);
      }
    }
  });

  return (
    <>
      <Helmet>
        <meta httpEquiv="Content-Security-Policy" content={getCspMeta()}></meta>
      </Helmet>
      <Loader
        loading={isUnderMaintenanceLoading || isHealthyLoading}
        loader={<LoadingSpinner size="large" />}
      >
        {isInvalidSession && <InvalidSession />}
        {!isInvalidSession && !isHealthy && <UnhealthyApp />}
        {!isInvalidSession && isHealthy && isUnderMaintenance && (
          <MaintenanceApp />
        )}
        {!isInvalidSession && isHealthy && !isUnderMaintenance && (
          <HealthyApp />
        )}
      </Loader>
    </>
  );
}
